import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  isDarkMode?: boolean;
}

const navLinks = [
  { label: 'Work', href: '/work' },
  { label: 'Background', href: '/background' },
  // { label: 'About', href: '/about' }
];

// Helper to determine if a color is dark
function isColorDark(rgb: string) {
  const match = rgb.match(/rgba?\((\d+), (\d+), (\d+)/);
  if (!match) return false;
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

function getEffectiveBgColor(el: HTMLElement | null): string {
  while (el && el !== document.body) {
    const bg = window.getComputedStyle(el).backgroundColor;
    if (bg && !bg.endsWith('0)')) { // not fully transparent
      return bg;
    }
    el = el.parentElement;
  }
  // fallback to body
  return window.getComputedStyle(document.body).backgroundColor;
}

const Header: React.FC<HeaderProps> = () => {
  const [isOnDark, setIsOnDark] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('header');
      if (!header) return;
      const headerRect = header.getBoundingClientRect();
      const x = (headerRect.left + headerRect.right) / 2;
      const y = headerRect.bottom + 1;
      let el = document.elementFromPoint(x, y) as HTMLElement | null;
      const bg = getEffectiveBgColor(el);
      setIsOnDark(isColorDark(bg));
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    setTimeout(handleScroll, 0);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 py-5 duration-1000 bg-black text-white md:bg-transparent ${isOnDark ? 'md:text-white' : 'md:text-black'}`}
    >
      <div className="w-full flex justify-between items-center px-4 sm:px-8 lg:px-16">
        <div className="flex items-center">
          <Link to="/" className="text-[36px]">Manish</Link>
        </div>
        {/* Desktop Nav */}
        <nav className="hidden md:flex space-x-4">
          {navLinks.map(link => (
            <Link 
              key={link.label} 
              to={link.href} 
              className={`rounded-full transition-all duration-300 text-center ${
                location.pathname.toLowerCase() === link.href.toLowerCase()
                  ? 'bg-white text-black' 
                  : 'opacity-100 hover:opacity-100'
              } ${
                link.label === 'Work' ? 'px-6 py-2' :
                link.label === 'Background' ? 'px-4 py-2' :
                'px-5 py-2'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        {/* Hamburger */}
        <button
          className="md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className={`md:hidden fixed inset-0 bg-black/90 z-40 flex flex-col items-center justify-center space-y-8 transition-all px-8`}>
          {/* Close button */}
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X size={32} />
          </button>
          {navLinks.map(link => (
            <Link
              key={link.label}
              to={link.href}
              className={`text-2xl font-semibold transition-opacity text-white ${
                location.pathname === link.href ? 'opacity-100' : 'opacity-90 hover:opacity-100'
              }`}
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;